import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import MsgReader from 'msgreader';
import { applyHighlightsToElement, renderHighlightedPlainText } from './highlight';

function formatMaybeDate(value: any): string {
  if (value == null) return '';
  if (value instanceof Date && !isNaN(value.getTime())) return value.toLocaleString();

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Heuristic: seconds vs milliseconds
    const ms = value < 1_000_000_000_000 ? value * 1000 : value;
    const d = new Date(ms);
    if (!isNaN(d.getTime())) return d.toLocaleString();
    return String(value);
  }

  if (typeof value === 'string') {
    const v = value.trim();
    if (!v) return '';
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleString();
    return v;
  }

  try {
    return String(value);
  } catch {
    return '';
  }
}

export function MsgViewer({ url, height = 'calc(85vh - 44px)', highlightText }: { url: string; height?: string | number; highlightText?: string | null }) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const htmlBodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);

    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error('Failed to fetch MSG');
        const buf = await resp.arrayBuffer();
        const reader = new (MsgReader as any)(buf);
        const fileData = reader.getFileData();
        if (cancelled) return;
        setData(fileData);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load MSG');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  const subject = data?.subject || '';
  const from = data?.senderEmail || data?.senderName || '';
  const to = data?.recipients?.map((r: any) => r.email || r.name).filter(Boolean).join('; ') || '';
  const dateRaw = data?.messageDeliveryTime
    ?? data?.clientSubmitTime
    ?? data?.sentOn
    ?? data?.sentTime
    ?? data?.date
    ?? data?.deliveryTime
    ?? data?.creationTime
    ?? data?.lastModificationTime
    ?? data?.receivedTime
    ?? '';

  const parseDateFromHeaders = (headersRaw: any): string => {
    if (!headersRaw) return '';
    const s = typeof headersRaw === 'string' ? headersRaw : '';
    if (!s.trim()) return '';
    const m = s.match(/^date\s*:\s*(.+)$/im);
    if (!m?.[1]) return '';
    return formatMaybeDate(m[1].trim());
  };

  let date = formatMaybeDate(dateRaw);
  if (!date) {
    // Some MSGs contain a raw headers string; try extracting a Date header.
    date = parseDateFromHeaders(data?.headers)
      || parseDateFromHeaders(data?.Headers)
      || parseDateFromHeaders(data?.transportMessageHeaders)
      || parseDateFromHeaders(data?.TransportMessageHeaders);
  }
  const htmlBody = data?.bodyHTML || data?.bodyHtml || '';
  const textBody = data?.body || '';

  useEffect(() => {
    if (!htmlBodyRef.current) return;
    if (!htmlBody) return;
    applyHighlightsToElement(htmlBodyRef.current, highlightText);
  }, [htmlBody, highlightText]);

  if (error) return <div style={{ padding: 12, color: 'red' }}>{error}</div>;
  if (!data) return <div style={{ padding: 12 }}>Loading email…</div>;

  return (
    <div style={{ height, overflow: 'auto', padding: 12, fontSize: 13, flex: 1, minHeight: 0 }}>
      <div style={{ border: '1px solid #e0e6ed', borderRadius: 6, padding: 10, background: '#f7fafd', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}><div style={{ width: 70, fontWeight: 600 }}>From:</div><div style={{ flex: 1, wordBreak: 'break-word' }}>{renderHighlightedPlainText(String(from), highlightText)}</div></div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}><div style={{ width: 70, fontWeight: 600 }}>To:</div><div style={{ flex: 1, wordBreak: 'break-word' }}>{renderHighlightedPlainText(String(to), highlightText)}</div></div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}><div style={{ width: 70, fontWeight: 600 }}>Date:</div><div style={{ flex: 1, wordBreak: 'break-word' }}>{renderHighlightedPlainText(String(date), highlightText)}</div></div>
        <div style={{ display: 'flex', gap: 8 }}><div style={{ width: 70, fontWeight: 600 }}>Subject:</div><div style={{ flex: 1, wordBreak: 'break-word' }}>{renderHighlightedPlainText(String(subject), highlightText)}</div></div>
      </div>

      {htmlBody ? (
        <div ref={htmlBodyRef} style={{ fontSize: 14, lineHeight: 1.35 }} dangerouslySetInnerHTML={{ __html: htmlBody }} />
      ) : (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'system-ui, Arial, sans-serif' }}>{renderHighlightedPlainText(textBody, highlightText)}</pre>
      )}
    </div>
  );
}
